import React from "react";
import { useParams } from "react-router-dom";
import FastBreadcrumbs from "../../shared/FastBreadcrumbs";
import Layout from "../../shared/Layout";
import { Box, Grid, LinearProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CourseDataInfo } from "../../types";
import { courseService } from "../../services/course.service";
import { CourseHeaderPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseHeaderPreview";
import { CourseDescriptionPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseDescriptionPreview";
import { CourseLearningObjectivesPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseLearningObjectivesPreview";
import { CourseMaterialsPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseMaterialsPreview";
import { CourseRequirementsPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseRequirementsPreview";
import { CourseRightBlockPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseRightBlockPreview";
import { CourseIncludesPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseIncludesPreview";
import { CourseRecommendationList } from "../Home/components/common/CoursePreview/componentsPreview/CourseRecommendationList";
import { CourseTeacherPreview } from "../Home/components/common/CoursePreview/componentsPreview/CourseTeacherPreview";
import "./CoursePage.scss";

const useFetchCourseInfo = (courseId: string) => {
  return useQuery<CourseDataInfo | undefined, Error>({
    queryKey: ["courseDetails", courseId],
    queryFn: () => courseService.getCourseInfo(courseId),
  });
};

const CoursePage = () => {
  const { courseId } = useParams<{ courseId?: string }>();
  const {
    data: courseInfo,
    isLoading,
    isError,
    error,
  } = useFetchCourseInfo(courseId || "");

  const breadcrumbs = [
    { name: "Главная", link: "/" },
    { name: "Курсы", link: "/courses" },
  ];

  if (isLoading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}>
        <Typography>Ошибка: {error?.message}</Typography>
      </Box>
    );
  }

  if (!courseInfo) {
    return (
      <Layout>
        <Box>Курс не найден</Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <FastBreadcrumbs links={breadcrumbs} className="bread-crumbs" />

      <Box
        sx={{
          display: "flex",
          minHeight: "calc(100vh - 68px)",
          pt: "24px",
          "@media (max-width: 1000px)": {
            pt: "0",
            width: "100%",
          },
        }}>
        <Grid
          container
          spacing="24px"
          sx={{
            "@media (max-width:1000px)": {
              gridTemplateColumns: "1fr",
              gap: "16px",
              width: "100%",
              ml: 0,
            },
          }}>
          <Grid
            item
            xs={8}
            sx={{
              overflowX: "auto",
              paddingRight: "24px",
              maxHeight: "100vh",
              "@media (max-width:1000px)": {
                padding: "35px 16px 0 !important",
                overflow: "visible",
                maxHeight: "unset",
                maxWidth: "unset",
                width: "100%",
                flexBasis: "unset",
              },
            }}>
            <CourseHeaderPreview courseDetails={courseInfo} />
            <CourseLearningObjectivesPreview learningObjectives={courseInfo} />
            <CourseMaterialsPreview courseMaterials={courseInfo.content} />
            <CourseRequirementsPreview
              courseRequirements={courseInfo.requirements}
            />
            <CourseDescriptionPreview courseDetails={courseInfo} />
            {courseInfo.recommendations && (
              <CourseRecommendationList
                recommendationsList={courseInfo.recommendations}
              />
            )}
            <CourseTeacherPreview courseDetails={courseInfo.teacher} />
          </Grid>

          <Grid
            item
            xs={4}
            sx={{
              "@media (max-width:1000px)": {
                width: "100%",
                maxWidth: "unset",
                padding: "16px 16px 0 16px !important",
                flexBasis: "unset",
              },
            }}>
            <CourseRightBlockPreview
              courseId={courseId}
              courseDetails={courseInfo}
            />
            <CourseIncludesPreview courseDetails={courseInfo} />
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};
export default CoursePage;
